import {
  selectMultipartContext,
  setMarkerWellMappings,
  updateMarkerWellMappings,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type {
  MappedEntityType,
  MarkerSetupType,
  MultipartUploadedFile,
  WellListItem,
} from '@core/core/types'
import { useEffect } from 'react'

import { getDistinctColumnValues } from './helper'
import type { MarkersParsedData, UseWellNamesMapperType } from './types'

const emptyWellsList: WellListItem[] = []
const emptyMappings: MappedEntityType[] = []

function createMappingEntities(
  wellNames: string[],
  wellsForMapping: WellListItem[],
): MappedEntityType[] {
  return wellNames.map(wellName => {
    const existing = wellsForMapping.find(projectWell => wellName === projectWell.wellName)
    return {
      name: wellName,
      status: existing ? 'success' : 'notmapped',
      mappedTo: existing,
    }
  })
}

export const useWellNamesMapper = (
  file: MultipartUploadedFile,
  parsedData: MarkersParsedData,
  setup: MarkerSetupType,
): UseWellNamesMapperType => {
  const { fileId } = file
  const dispatch = useAppDispatch()
  const { dataTable } = parsedData
  const { wellNameColumn } = setup

  const mappedWells = file.mappedEntities ?? emptyMappings
  const mappingContext = useAppSelector(selectMultipartContext)
  const wellsForMapping = mappingContext.wellsForMapping ?? emptyWellsList

  useEffect(() => {
    if (dataTable === undefined) {
      dispatch(setMarkerWellMappings(fileId, []))
    } else {
      new Promise<string[]>(resolve => {
        resolve(getDistinctColumnValues(dataTable, wellNameColumn))
      })
        .then(wellNames => createMappingEntities(wellNames, wellsForMapping))
        .then(mappings => {
          dispatch(setMarkerWellMappings(fileId, mappings))
        })
    }
  }, [fileId, wellsForMapping, dataTable, wellNameColumn])

  const updateWellMapping: UseWellNamesMapperType['updateWellMapping'] = (
    wellForMapping: MappedEntityType,
  ) => {
    dispatch(updateMarkerWellMappings(fileId, [wellForMapping]))
  }

  return {
    mappedWells,
    wellsForMapping,
    updateWellMapping,
  }
}
